import { Label } from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import type { ReactNode } from 'react';

export interface FormFieldProps {
  label: ReactNode;
  htmlFor: string;
  required?: boolean;
  description?: ReactNode;
  error?: string | undefined;
  className?: string;
  /** Control first, label after it (checkbox and switch rows). */
  inline?: boolean;
  children: ReactNode;
}

/** Ids for `aria-describedby`; pass the same `htmlFor` to `FormField` and to the control. */
export function fieldA11y(htmlFor: string, options: { description?: unknown; error?: unknown }) {
  const ids = [
    options.description ? `${htmlFor}-description` : null,
    options.error ? `${htmlFor}-error` : null,
  ].filter((id): id is string => id !== null);
  return {
    id: htmlFor,
    'aria-describedby': ids.length > 0 ? ids.join(' ') : undefined,
    'aria-invalid': options.error ? true : undefined,
  } as const;
}

/** Labelled control with optional description and inline error (mirrors `FieldRow` for ad-hoc forms). */
export function FormField({
  label,
  htmlFor,
  required,
  description,
  error,
  className,
  inline,
  children,
}: FormFieldProps) {
  const labelNode = (
    <Label htmlFor={htmlFor}>
      {label}
      {required ? (
        <span aria-hidden="true" className="text-red-600 dark:text-red-400">
          *
        </span>
      ) : null}
    </Label>
  );
  return (
    <div className={cn('space-y-1.5', className)} data-slot="form-field">
      {inline ? (
        <div className="flex items-center gap-2">
          {children}
          {labelNode}
        </div>
      ) : (
        <>
          {labelNode}
          {children}
        </>
      )}
      {description ? (
        <p id={`${htmlFor}-description`} className="text-fg-muted text-xs">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-red-600 dark:text-red-400 text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}
